import dayjs from 'dayjs';
import { analytics } from '../analytics/analytics';
import { AnalyticsEvent } from '../analytics/analyticsEvent';
import { taskCompletedProperties } from '../analytics/analyticsHelpers';
import { WorkspaceAccessResponse } from '../api/workspace';
import { EditField } from '../components/TaskManagement/ActionPanel/EditFieldsDropdown';
import { PriorityOptions } from '../components/TaskTable/helpers/helper';
import { SprintStatusType } from '../components/UI/SprintStatusLabel';
import { queryClient } from '../queryClient';
import { usePlanningSession } from '../serverState/planning';
import { useGetTasks, useUpdateTasksMutation, useDeleteTasksMutation } from '../serverState/tasks';
import { UpdateSpaceTasksModel } from '../api/types/workspace.types';
import { useGetWorkspace } from '../serverState/workspace';
import { Epic } from '../types/Epic';
import Task, { TaskStage } from '../types/Task';
import { TaskStatus, TaskStatusGroup } from '../types/TaskStatus';
import { TaskType } from '../types/TaskType';
import { SpaceLabel } from '../types/TaskLabel';
import { WorkspaceEstimationType, WorkspaceFramework } from '../types/Workspace';
import { WorkspaceViewGroup } from '../types/WorkspaceView';
import { getWorkspaceViewGroup } from '../common/viewUtils';

/* eslint-disable no-case-declarations */

// The single stage a view group displays. Returns null for groups that span
// multiple stages (epics), where we never auto-close the batch panel.
const stageShownByGroup = (group: WorkspaceViewGroup): TaskStage | null => {
  switch (group) {
    case WorkspaceViewGroup.triage:
      return 'triage';
    case WorkspaceViewGroup.backlog:
      return 'backlog';
    case WorkspaceViewGroup.planning:
      return 'planning';
    case WorkspaceViewGroup.sprint:
    case WorkspaceViewGroup.kanban:
      return 'sprint';
    case WorkspaceViewGroup.archive:
      return 'archive';
    default:
      return null;
  }
};

// A stage-changing batch edit removes the tasks from the current module (so the
// panel should deselect + close) when the resulting stage is no longer shown by
// the active view group. Archiving always closes — archived tasks are evicted
// from the task cache across every view. (DEV-2528)
const shouldCloseAfterStageChange = (nextStage: TaskStage | undefined): boolean => {
  if (!nextStage) return false;
  if (nextStage === 'archive') return true;
  const shownStage = stageShownByGroup(getWorkspaceViewGroup(window.location.pathname));
  return shownStage !== null && nextStage !== shownStage;
};
export const useBatchActions = (
  spaceId: string | undefined,
  onActionSuccess: (message: string, hidePanel: boolean) => void,
  onActionInfo: (info: string) => void,
  onActionFailure: (error: string) => void
) => {
  const workspaceQuery = useGetWorkspace();
  const planningSessionQuery = usePlanningSession();
  const tasksQuery = useGetTasks(Number(workspaceQuery.data?.id));
  const updateTasksMutation = useUpdateTasksMutation();
  const deleteTasksMutation = useDeleteTasksMutation();
  // Same signatures as the bare API fns, but tracked by the mutation counter.
  const updateTasks = (spaceId: number, taskIds: number[], changes: UpdateSpaceTasksModel) =>
    updateTasksMutation.mutateAsync({ spaceId, taskIds, changes });
  const deleteTasks = (spaceId: number, taskIds: number[]) => deleteTasksMutation.mutateAsync({ spaceId, taskIds });

  const onBatchEdit = async (
    fieldType: EditField,
    field:
      | TaskType
      | SprintStatusType
      | Epic
      | WorkspaceAccessResponse
      | TaskStatus
      | SpaceLabel[]
      | (Date | undefined)
      | PriorityOptions
      | number
      | TaskStage,
    selectedItems: number[],
    action?: 'add' | 'remove'
  ) => {
    const currentSpaceId = workspaceQuery.data?.id;

    const updatedFields: Partial<Task> = {};
    let successMessage = 'updated';
    switch (fieldType) {
      case EditField.taskType:
        updatedFields.type = field as TaskType;
        await updateTasks(Number(currentSpaceId), selectedItems, { type: field as TaskType });
        break;
      case EditField.sprintStatus:
        if (field === 'planning') {
          updatedFields.stage = 'planning';
          updatedFields.sprintId = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { stage: 'planning' });
          successMessage = 'moved to Planning';
        } else if (field === 'sprint' || field === 'kanban') {
          const workspace = workspaceQuery.data;

          // if current workspace doesn't have active sprint show alert saying sprint has not started
          if (!workspace?.activeSprintId) {
            onActionInfo('Sprint has not started yet!');
            return;
          }
          updatedFields.stage = 'sprint';
          updatedFields.sprintId = Number(workspace.activeSprintId);
          updatedFields.planningSessionId = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { stage: 'sprint' });
          successMessage = `moved to ${workspaceQuery.data?.framework === WorkspaceFramework.kanban ? 'Kanban' : 'Sprint'}`;
        } else if (field === 'none') {
          updatedFields.stage = 'backlog';
          updatedFields.sprintId = null;
          updatedFields.planningSessionId = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { stage: 'backlog' });
          successMessage = 'moved to Backlog';
        } else if (field === 'triage') {
          updatedFields.stage = 'triage';
          updatedFields.sprintId = null;
          updatedFields.planningSessionId = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { stage: 'triage' });
          successMessage = 'moved to Triage';
        } else if (field === 'archive') {
          updatedFields.stage = 'archive';
          updatedFields.sprintId = null;
          updatedFields.planningSessionId = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { stage: 'archive' });
          successMessage = 'archived';
        }
        break;
      case EditField.stage: {
        const nextStage = field as TaskStage;
        updatedFields.stage = nextStage;
        if (nextStage === 'sprint') {
          const activeSprintId = workspaceQuery.data?.activeSprintId;
          if (activeSprintId) {
            updatedFields.sprintId = Number(activeSprintId);
          }
          updatedFields.planningSessionId = null;
        } else if (nextStage === 'planning') {
          updatedFields.sprintId = null;
        } else {
          updatedFields.sprintId = null;
          updatedFields.planningSessionId = null;
        }
        await updateTasks(Number(currentSpaceId), selectedItems, { stage: nextStage });
        successMessage =
          nextStage === 'archive'
            ? 'archived'
            : nextStage === 'backlog'
              ? 'moved to Backlog'
              : `moved to ${nextStage.charAt(0).toUpperCase()}${nextStage.slice(1)}`;
        break;
      }
      case EditField.epic:
        const epicId = (field as Epic).id;
        updatedFields.epicId = epicId;
        await updateTasks(Number(currentSpaceId), selectedItems, { epicId });
        break;
      case EditField.owner:
        if (action === 'remove') {
          updatedFields.assignee = null;
          await updateTasks(Number(currentSpaceId), selectedItems, { assignee: null });
          successMessage = 'removed owner from';
        } else {
          const assignee = (field as WorkspaceAccessResponse).userId;
          updatedFields.assignee = assignee;
          await updateTasks(Number(currentSpaceId), selectedItems, { assignee });
        }
        break;
      case EditField.status:
        const statusId = (field as TaskStatus).id;
        updatedFields.statusId = statusId;
        await updateTasks(Number(currentSpaceId), selectedItems, { statusId });
        // Analytics event
        const taskStatus = workspaceQuery.data?.TaskStatus?.find((status) => status.id === statusId);
        if (taskStatus?.group === TaskStatusGroup.completed) {
          tasksQuery.data
            ?.filter((task) => selectedItems.includes(task.id))
            .forEach((task) => {
              analytics.track(AnalyticsEvent.taskCompleted, taskCompletedProperties(task, workspaceQuery.data));
            });
        }
        break;
      case EditField.label:
        const labels = field as SpaceLabel[];
        const labelIds = labels.map((label) => label.id);

        if (action === 'remove') {
          // Handle label removal
          await updateTasks(Number(currentSpaceId), selectedItems, {
            labelIds,
            action: 'remove',
          });
          successMessage = `removed label${labels.length > 1 ? 's' : ''} from`;
        } else {
          // Handle label addition (default behavior)
          updatedFields.TaskLabels = labels.map((label) => ({ labelId: label.id, taskId: selectedItems[0], id: 0 }));
          await updateTasks(Number(currentSpaceId), selectedItems, { labelIds });
          successMessage = `added label${labels.length > 1 ? 's' : ''} to`;
        }
        break;
      case EditField.startDate:
        const startDate = field as Date | undefined;
        updatedFields.startDate = startDate || null;
        const updateTasksStartDateResult = await updateTasks(Number(currentSpaceId), selectedItems, {
          startDate: startDate || null,
        });

        const updatedStartDateTasks = updateTasksStartDateResult.data.tasks;

        if (updatedStartDateTasks.length === 0) {
          onActionFailure('Start date must be before target date.');
          return;
        } else if (updatedStartDateTasks.length < selectedItems.length) {
          updateTasksDate(updatedStartDateTasks, EditField.startDate, startDate, Number(currentSpaceId));
          onActionInfo('Start date(s) updated where applicable.');
          return;
        }
        break;

      case EditField.deadline:
        const deadline = field as Date | undefined;
        updatedFields.deadline = deadline || null;
        const updateTasksDeadlineResult = await updateTasks(Number(currentSpaceId), selectedItems, {
          deadline: deadline || null,
        });
        const updatedDeadlineTasks = updateTasksDeadlineResult.data.tasks;

        if (updatedDeadlineTasks.length === 0) {
          onActionFailure('Target date must be after start date.');
          return;
        } else if (updatedDeadlineTasks.length < selectedItems.length) {
          updateTasksDate(updatedDeadlineTasks, EditField.deadline, deadline, Number(currentSpaceId));
          onActionInfo('Target date(s) updated where applicable.');
          return;
        }
        break;
      case EditField.priority:
        updatedFields.priority = field as PriorityOptions;
        await updateTasks(Number(currentSpaceId), selectedItems, { priority: field as PriorityOptions });
        break;
      case EditField.estimate:
        if (action === 'remove') {
          if (workspaceQuery.data?.estimationType === WorkspaceEstimationType.tshirtSizes) {
            updatedFields.estimate = null;
            await updateTasks(Number(currentSpaceId), selectedItems, { estimate: null });
          } else {
            updatedFields.storyPoints = null;
            await updateTasks(Number(currentSpaceId), selectedItems, { storyPoints: null });
          }
          successMessage = 'removed estimate from';
        } else if (workspaceQuery.data?.estimationType === WorkspaceEstimationType.tshirtSizes) {
          updatedFields.estimate = field as number;
          await updateTasks(Number(currentSpaceId), selectedItems, { estimate: field as number });
        } else {
          updatedFields.storyPoints = field as number;
          await updateTasks(Number(currentSpaceId), selectedItems, { storyPoints: field as number });
        }
        break;
      default:
        break;
    }

    // Per-task optimistic field updater, applied to whichever cache holds the task.
    const applyFieldUpdate = (task: Task): Task => {
      if (!selectedItems.includes(task.id)) return task;
      switch (fieldType) {
        case EditField.label:
          const existingLabels = task.TaskLabels || [];

          if (action === 'remove') {
            // Remove specified labels
            const labelsToRemove = field as SpaceLabel[];
            const labelIdsToRemove = new Set(labelsToRemove.map((l) => l.id));
            const filteredLabels = existingLabels.filter((tl) => !labelIdsToRemove.has(tl.labelId));

            return {
              ...task,
              TaskLabels: filteredLabels,
            };
          } else {
            // Add new labels (default behavior)
            const newLabels =
              updatedFields.TaskLabels?.map((tl) => ({
                ...tl,
                taskId: task.id,
              })) || [];

            const newLabelIds = new Set(newLabels.map((tl) => tl.labelId));

            // Keep existing labels that aren't in the new labels list
            const preservedLabels = existingLabels.filter((tl) => !newLabelIds.has(tl.labelId));

            // Merge preserved labels with new labels
            const mergedLabels = [...preservedLabels, ...newLabels];

            return {
              ...task,
              ...updatedFields,
              TaskLabels: mergedLabels,
            };
          }
        case EditField.startDate:
          const startDate = field as Date | undefined;
          if (startDate && task.deadline && dayjs(task.deadline) < dayjs(startDate)) {
            return task;
          } else {
            return {
              ...task,
              ...updatedFields,
            };
          }
        case EditField.deadline:
          const deadline = field as Date | undefined;
          if (deadline && task.startDate && dayjs(task.startDate) > dayjs(deadline)) {
            return task;
          } else {
            return {
              ...task,
              ...updatedFields,
            };
          }
        default:
          return {
            ...task,
            ...updatedFields,
          };
      }
    };

    // The Archive view reads a separate cache (['tasks', id, 'archive']). A batch
    // edit made from any view must keep both caches consistent, otherwise archived
    // rows go stale (DEV-2528). A task lives in exactly one cache, so we patch the
    // one it's in and evict it on an archive-boundary crossing.
    const defaultKey = ['tasks', Number(currentSpaceId)];
    const archiveKey = ['tasks', Number(currentSpaceId), 'archive'];
    const archivedTasks = (queryClient.getQueryData(archiveKey) as Task[]) || [];
    const nextStage = updatedFields.stage;
    const enteringArchive = nextStage === 'archive';
    const leavingArchive =
      !!nextStage && nextStage !== 'archive' && selectedItems.some((id) => archivedTasks.some((t) => t.id === id));

    queryClient.setQueryData(defaultKey, (oldData: Task[]) => {
      if (!oldData) return oldData;
      const filtered = enteringArchive ? oldData.filter((t) => !selectedItems.includes(t.id)) : oldData;
      return filtered.map(applyFieldUpdate);
    });

    queryClient.setQueryData(archiveKey, (oldData: Task[]) => {
      if (!oldData) return oldData;
      const filtered = leavingArchive ? oldData.filter((t) => !selectedItems.includes(t.id)) : oldData;
      return filtered.map(applyFieldUpdate);
    });

    // On a boundary crossing the row moves between caches; refetch the destination
    // so it appears there with fresh server state.
    if (enteringArchive) void queryClient.invalidateQueries({ queryKey: archiveKey });
    if (leavingArchive) void queryClient.invalidateQueries({ queryKey: defaultKey });

    onActionSuccess(
      `${selectedItems.length} ${selectedItems.length > 1 ? 'tasks' : 'task'} ${successMessage}.`,
      shouldCloseAfterStageChange(updatedFields.stage)
    );
  };

  const onDeleteTasks = (selectedItems: number[]) => {
    const currentSpaceId = workspaceQuery.data?.id;

    deleteTasks(Number(currentSpaceId), selectedItems)
      .then(() => {
        // Identify deleted epics to clean up epicId references
        const currentEpics = (queryClient.getQueryData(['epics', Number(currentSpaceId)]) as Epic[]) || [];
        const deletedEpicIds = currentEpics
          .filter((epic) => selectedItems.includes(epic.id))
          .map((epic) => epic.id);

        queryClient.setQueryData(['tasks', Number(currentSpaceId)], (oldData: Task[]) => {
          let filtered = oldData.filter((task) => !selectedItems.includes(task.id));
          if (deletedEpicIds.length > 0) {
            filtered = filtered.map((task) =>
              task.epicId && deletedEpicIds.includes(task.epicId) ? { ...task, epicId: null } : task
            );
          }
          return filtered;
        });
        // Deleted archived tasks live in the separate archive cache (DEV-2528).
        queryClient.setQueryData(['tasks', Number(currentSpaceId), 'archive'], (oldData: Task[]) =>
          oldData ? oldData.filter((task) => !selectedItems.includes(task.id)) : oldData
        );
        queryClient.setQueryData(['epics', Number(currentSpaceId)], (oldData: Epic[]) => {
          return oldData.filter((epic) => !selectedItems.includes(epic.id));
        });

        // Invalidate subtask queries for deleted parent tasks
        selectedItems.forEach((id) => {
          void queryClient.invalidateQueries({ queryKey: ['taskSubtasks', id] });
        });

        onActionSuccess(
          `${selectedItems.length} ${selectedItems.length > 1 ? 'tasks' : 'task'} deleted successfully.`,
          true
        );
      })
      .catch(() => {
        onActionFailure('Failed to delete tasks');
      });
  };

  const updateTasksDate = (
    taskIds: number[],
    fieldType: EditField.startDate | EditField.deadline,
    dateValue: Date | undefined | null,
    currentSpaceId: number
  ) => {
    queryClient.setQueryData(['tasks', Number(currentSpaceId)], (oldData: Task[]) => {
      return oldData.map((task) => {
        if (taskIds.includes(task.id)) {
          const updatedFields: Partial<Task> = {};

          if (fieldType === EditField.startDate) {
            updatedFields.startDate = dateValue || null;
          } else if (fieldType === EditField.deadline) {
            updatedFields.deadline = dateValue || null;
          }

          return {
            ...task,
            ...updatedFields,
          };
        }
        return task;
      });
    });
  };

  return {
    onBatchEdit,
    onDeleteTasks,
  };
};
